/**
 * Cosmetic mastery rewards (spec 6.5): registry-driven, all local, all
 * EARNED — never time-gated, never purchasable. M3 ships the ship-colorway
 * set; trail styles join with the M4 trail system (registry entries exist so
 * unlock conditions are already banked when trails render).
 */
import type { SaveStore } from "./saveStore";
import { CAMPAIGN } from "../levels";
import { medalFor } from "./medals";

export interface CosmeticDef {
  id: string;
  kind: "ship" | "trail";
  name: string;
  /** Hull / trail emissive color. */
  colorHex: string;
  /** Short "how to earn" copy shown while locked. */
  earn: string;
  condition: (save: SaveStore) => boolean;
  /** Trail styles (R2-M4 VFX-9): ribbon width multiplier + translucency. */
  trailWidth?: number;
  trailAlpha?: number;
}

function worldComplete(save: SaveStore, world: number): boolean {
  return CAMPAIGN.filter((l) => l.world === world).every(
    (l) => save.getLevel(l.id)?.completed ?? false,
  );
}

function goldCount(save: SaveStore): number {
  let n = 0;
  for (const level of CAMPAIGN) {
    const entry = save.getLevel(level.id);
    if (!entry || entry.bestTimeMs === undefined || !level.medalTimesMs) continue;
    const m = medalFor(entry.bestTimeMs, level.medalTimesMs);
    if (m === "gold" || m === "author") n++;
  }
  return n;
}

function anyDeathless(save: SaveStore): boolean {
  return CAMPAIGN.some((l) => save.getLevel(l.id)?.deathless ?? false);
}

export const COSMETICS: CosmeticDef[] = [
  { id: "default", kind: "ship", name: "Glider", colorHex: "#9BEBF7",
    earn: "", condition: () => true },
  { id: "ion-glider", kind: "ship", name: "Ion Glider", colorHex: "#F472B6",
    earn: "Complete every World 1 level", condition: (s) => worldComplete(s, 1) },
  { id: "redline-hull", kind: "ship", name: "Redline Hull", colorHex: "#FB923C",
    earn: "Complete every World 2 level", condition: (s) => worldComplete(s, 2) },
  { id: "pure-light", kind: "ship", name: "Pure Light", colorHex: "#FFF7D6",
    earn: "Any deathless clear", condition: anyDeathless },
  { id: "gold-standard", kind: "ship", name: "Gold Standard", colorHex: "#FFD700",
    earn: "Earn 3 gold medals", condition: (s) => goldCount(s) >= 3 },
  { id: "streak-flame", kind: "ship", name: "Streak Flame", colorHex: "#A78BFA",
    earn: "7-day daily streak", condition: (s) => s.daily.streak >= 7 },
  // trail styles (R2-M4 VFX-9): distinct width + translucency per style
  { id: "trail-default", kind: "trail", name: "Ion Trail", colorHex: "#22D3EE",
    earn: "", condition: () => true, trailWidth: 1, trailAlpha: 0.6 },
  { id: "trail-gold", kind: "trail", name: "Gilded Trail", colorHex: "#FFD700",
    earn: "Earn 6 gold medals", condition: (s) => goldCount(s) >= 6,
    trailWidth: 0.7, trailAlpha: 0.85 },
  { id: "trail-pure", kind: "trail", name: "Pure Trail", colorHex: "#FFFFFF",
    earn: "Any deathless clear", condition: anyDeathless,
    trailWidth: 1.6, trailAlpha: 0.3 },
];

export function cosmeticById(id: string): CosmeticDef | undefined {
  return COSMETICS.find((c) => c.id === id);
}

/** Evaluate conditions; persist and return any NEWLY unlocked cosmetics. */
export function evalUnlocks(save: SaveStore): CosmeticDef[] {
  const fresh: CosmeticDef[] = [];
  for (const c of COSMETICS) {
    if (!save.cosmetics.unlocked.includes(c.id) && c.condition(save)) {
      save.cosmetics.unlocked.push(c.id);
      fresh.push(c);
    }
  }
  if (fresh.length > 0) save.persist();
  return fresh;
}
